import React, { useEffect, useMemo, useState } from 'react';
import { FaSearch, FaDownload } from 'react-icons/fa';

import { capitalize, formatDateTime } from 'v3/utils/formatter';
import { useTravel } from 'v3/pages/Travel';
import { useSnackbar } from 'v3/components/Snackbar';
import { cooperplaceApi } from 'services/api';

import Text from 'v3/components/Text';
import Radio from 'v3/components/Radio';
import Modal from 'v3/components/Modal';
import PDFView from 'v3/components/PDFView';
import Loader from 'v3/components/Loader';
import { validatePermission } from 'actions/index';
import Loading from 'components/Loading';
import * as Styled from './styles';

const AUTOMATIONS = {
  SEND_ATTACHMENT_KEPYA: 'Kepya',
  SEND_ATTACHMENT_TRUCKWEB: 'Truckweb',
  SEND_ATTACHMENT_OK_ENTREGA: 'Ok Entrega',
};

const AttachmentLabel = {
  accept: 'Aceito por: ',
  reject: 'Rejeitado por: ',
  resend: 'Solicitado reenvio por: ',
};

function isDocument(attachment) {
  const clientName = attachment.docFilename ?? '';
  const colonIndex = clientName.indexOf(':');
  const ext = clientName.split('.').pop();
  const baseName =
    colonIndex !== -1
      ? clientName.substring(0, colonIndex)
      : clientName.substring(0, clientName.lastIndexOf('.'));

  return !!baseName && ext?.toLowerCase() === 'pdf';
}

export default function Attachment({
  document,
  onClickPreview,
  fetchDocuments,
  emittedId,
  setLoading,
}) {
  const [isHovering, setHovering] = useState(null);
  const [isShowingModal, setShowingModal] = useState(null);
  const [isInFlight, setInFlight] = useState({});
  const [status, setStatus] = useState({});

  const travel = useTravel();
  const snackbar = useSnackbar();

  const automations = useMemo(() => {
    const mapped = travel?.data?.settings
      ?.flatMap((automation) => automation?.automations)
      .map((auto) => AUTOMATIONS[auto?.slug]);

    return mapped.join(', ');
  }, [travel.data.settings]);

  const attachments = useMemo(
    () =>
      document.travelAttachments.map(att => ({
        ...att,
        obs: document.obsData,
        nfNumber: document.nfNumber,
      })),
    [document]
  );

  async function updateReview(newStatus, attachmentId) {
    try {
      setStatus(prev => ({ ...prev, [attachmentId]: newStatus }));
      setInFlight(prev => ({ ...prev, [attachmentId]: true }));

      await cooperplaceApi.put(`travel-attachments/${attachmentId}/review`, {
        status: newStatus,
      });

      let message;
      switch (newStatus) {
        case 'accept':
          message = `Anexo aceito com sucesso${
            automations ? `, enviado para ${automations}` : ''
          }`;
          break;
        case 'resend':
          message = 'Solicitação de reenvio realizada';
          break;
        case 'reject':
          message = 'Anexo rejeitado';
          break;
        default:
          message = 'Ação inesperada';
      }

      await fetchDocuments(emittedId, { setLoading, refetch: true });
      await travel.retrieveOccurrences();
      snackbar.show(<Text>{message}</Text>, { type: 'success' });
    } catch (ex) {
      const message =
        ex?.response?.data?.error ||
        ex?.response?.data?.message ||
        'Não foi possível alterar o status desse anexo';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setInFlight(prev => ({ ...prev, [attachmentId]: false }));
    }
  }

  useEffect(() => {
    if (!travel.isLoading) {
      const initial = {};
      attachments.forEach(att => {
        initial[att.id] = att.status;
      });
      setStatus(initial);
    }
  }, [attachments, travel.isLoading]);

  const handleClick = attachment => {
    if (
      attachment.path?.endsWith('.pdf') ||
      attachment.docFilename?.toLowerCase().endsWith('.pdf')
    ) {
      setShowingModal(attachment);
    } else {
      onClickPreview(attachment.id);
    }
  };

  return (
    <div>
      {/* Modal único para o attachment selecionado */}
      <Modal
        show={!!isShowingModal}
        onHide={() => setShowingModal(null)}
        dialogClassName="modal-90w"
        heading={
          <Text color="dark" type="header" weight="500">
            Visualizar Anexo
          </Text>
        }
        body={isShowingModal && <PDFView file={isShowingModal.path} />}
      />

      {/* Grid de anexos */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {attachments.map(attachment => (
          <div key={attachment.id} style={{ width: 270 }}>
            {!!attachment.nfNumber && (
              <Text type="label" color="dark">
                Nota fiscal {attachment.nfNumber}
              </Text>
            )}

            <div
              className="position-relative"
              onMouseEnter={() => setHovering(attachment.id)}
              onMouseLeave={() => setHovering(null)}
              style={{
                width: '100%',
                height: 160,
                background: `url(${
                  isDocument(attachment)
                    ? '/images/default-doc.png'
                    : attachment.path
                }) center center`,
                backgroundSize: 'cover',
              }}
            >
              {isHovering === attachment.id && (
                <Styled.Backdrop>
                  <button type="button" onClick={() => handleClick(attachment)}>
                    <FaSearch color="#fff" size={32} />
                  </button>
                  <a href={attachment.path} download>
                    <FaDownload color="#fff" size={32} />
                  </a>
                </Styled.Backdrop>
              )}

              {isInFlight[attachment.id] && (
                <Styled.Backdrop>
                  <Loader size="md" loading variant="light" />
                </Styled.Backdrop>
              )}

              <Styled.DateTime>
                <Text type="label" color="white">
                  {formatDateTime(attachment.createdAt)}
                </Text>
              </Styled.DateTime>
            </div>

            <div className="d-flex justify-content-center mt-1">
              {validatePermission('APROVACAO_POS_VENDA') && (
                <Radio.Group
                  value={status[attachment.id]}
                  horizontal
                  onChange={({ target }) =>
                    updateReview(target.value, attachment.id)
                  }
                  disabled={isInFlight[attachment.id]}
                >
                  <Radio value="accept">
                    <Text type="regular" color="dark">
                      Aceitar
                    </Text>
                  </Radio>
                  <Radio value="resend">
                    <Text type="regular" color="dark">
                      Reenviar
                    </Text>
                  </Radio>
                  <Radio value="reject">
                    <Text type="regular" color="dark">
                      Rejeitar
                    </Text>
                  </Radio>
                </Radio.Group>
              )}
            </div>

            <Styled.AttachmentDetailsContainer>
              <Text type="label" color="dark">
                Incluído por: {attachment?.userCreated?.username}
              </Text>
              {attachment?.userUpdated &&
                attachment?.status !== 'pending' &&
                (isInFlight[attachment.id] ? (
                  <Loading />
                ) : (
                  <Text type="label" color="dark">
                    {AttachmentLabel[attachment.status]}
                    {capitalize(attachment?.userUpdated?.username)} em{' '}
                    {formatDateTime(attachment?.updatedAt)}
                  </Text>
                ))}
            </Styled.AttachmentDetailsContainer>
          </div>
        ))}
      </div>
    </div>
  );
}
